import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexPlotOptions,
    ApexTooltip,
    ApexYAxis,
    ApexGrid,
    ApexLegend,
} from 'ng-apexcharts';

import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { GenericComponent } from '../generic/generic.component';

import { SidenavService } from 'src/app/shared/services/sidenav.service';

import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec6Gray,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColors,
} from 'src/app/shared/components/map/map.constants';
import { PathOptions } from 'leaflet';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    LV_AERIAL_BARE_NETWORK_RATE_MAP,
    LV_NETWORKS_AGES_EVOL_LENGHT_CHART,
    LV_NETWORKS_AGES_EVOL_LENGHT_FS_CHART,
    LV_NETWORKS_AGES_KPIS,
    LV_NETWORKS_AGES_RATE_COMPARISON_CHART,
    LV_NETWORKS_AVERAGE_AGES_CHART,
} from '../../assets/keys';

const KEYS = [
    LV_NETWORKS_AGES_KPIS,
    LV_NETWORKS_AVERAGE_AGES_CHART,
    LV_NETWORKS_AGES_EVOL_LENGHT_CHART,
    LV_NETWORKS_AGES_EVOL_LENGHT_FS_CHART,
    LV_NETWORKS_AGES_RATE_COMPARISON_CHART,
    LV_AERIAL_BARE_NETWORK_RATE_MAP,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    lvAerialBareNetworkLength?: number;
    lvAerialBareNetworkLengthEvolution?: number;

    lvAerialBareFsNetworkLength?: number;
    lvAerialBareFsNetworkLengthEvolution?: number;

    lvDefaultDated1946WorksRate?: number;
    lvDefaultDated1946WorksRateEvolution?: number;
}

interface AverageAgesChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    totalLvNetworkAges?: number;
    overheadLvNetworkAges?: number;
    twistedLvNetworkAges?: number;
    undergroundLvNetworkAges?: number;
    error?: boolean;
}

interface EvolLengthChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    bareOverheadNetworkLvRate?: number;
    bareOverheadLvLengthEr?: number;
    bareOverheadLvLengthRu?: number;
    error?: boolean;
}

interface EvolLengthFsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    lowSectionLvNetworkPercentage?: number;
    lowSectionLvLengthEr?: number;
    lowSectionLvLengthRu?: number;
    error?: boolean;
}

interface RateComparisonChartData {
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    templateUrl: './lv-networks-ages.component.html',
    styleUrls: ['./lv-networks-ages.component.scss'],
})
export class LvNetworksAgesComponent extends GenericComponent {
    public texts = CC_CONSTANTS.lvNetworks.tabs.aging.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_AGES_KPIS),
    ) as Signal<KpiData[]>;

    public averageAgesChartDataSignal: Signal<AverageAgesChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_AVERAGE_AGES_CHART),
    ) as Signal<AverageAgesChartData[]>;
    public averageAgesChartSeries: ApexAxisChartSeries = [];
    public averageAgesChartGrid: ApexGrid = { padding: { left: 30 } };
    public averageAgesChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public averageAgesChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.lvNetworksAverageAgesChart.yaxisTitleLeft,
            },
        },
    ];
    public averageAgesChartError: boolean = true;

    public evolLengthChartDataSignal: Signal<EvolLengthChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_AGES_EVOL_LENGHT_CHART),
    ) as Signal<EvolLengthChartData[]>;
    public evolLengthChartSeries: ApexAxisChartSeries = [];
    public evolAgesChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.lvNetworksEvolOverheadChart.yaxisTitleLeft,
            },
        },
    ];
    public evolLengthPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            columnWidth: '100%',
            barHeight: '100%',
            dataLabels: {
                position: 'center',
            },
        },
    };
    public evolLengthChartDataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public evolLengthChartGrid: ApexGrid = { padding: { right: 30 } };
    public evolLengthChartError: boolean = true;
    public evolLengthCustomTooltip: ApexTooltip['custom'];
    public evolLengthLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec1BlueLight, colorAec3Green, colorAec2BlueDark],
        },
    };

    public evolLengthFsChartDataSignal: Signal<EvolLengthFsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_AGES_EVOL_LENGHT_FS_CHART),
    ) as Signal<EvolLengthFsChartData[]>;
    public evolLengthFsChartSeries: ApexAxisChartSeries = [];
    public evolLengthFsChartDataLabels: ApexDataLabels = { enabled: false, enabledOnSeries: [] };
    public evolAgesFsChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.lvNetworksEvolOverheadChart.yaxisTitleLeft,
                offsetX: -23,
            },
            reversed: true,
        },
    ];
    public evolLengthFsPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            columnWidth: '100%',
            barHeight: '100%',
            dataLabels: {
                position: 'center',
            },
        },
    };
    public evolLengthFsChartGrid: ApexGrid = { padding: { right: 0 } };
    public evolLengthFsChartError: boolean = true;
    public evolLengthFsCustomTooltip: ApexTooltip['custom'];
    public evolLengthFsLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec1BlueLight, colorAec3Green, colorAec2BlueDark],
        },
    };

    public rateComparisonChartSeries: ApexAxisChartSeries = [];
    public rateComparisonChartDataSignal: Signal<RateComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(LV_NETWORKS_AGES_RATE_COMPARISON_CHART),
        ) as Signal<AverageAgesChartData[]>;
    public rateComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public rateComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(4),
            borderRadius: 1,
        },
    };
    public rateComparisonCustomTooltip: ApexTooltip['custom'] = function ({
        series,
        seriesIndex,
        dataPointIndex,
        w,
    }) {
        const precision =
            CC_CONSTANTS.lvNetworks.tabs.aging.texts.lvNetworksRateComparisonChart.precision;
        const dataSerie = w.globals.initialSeries[seriesIndex];
        const serieName = dataSerie.name;
        const serieColor = dataSerie.color;
        const dataPoint = dataSerie.data[dataPointIndex];
        const min = convertNumber(dataPoint.y[0], precision, false);
        const max = convertNumber(dataPoint.y[1], precision, false);

        const meanGoal = dataPoint.goals[0];
        const meanValue = convertNumber(meanGoal.value, precision, false);
        const meanName = meanGoal.name;
        const meanColor = meanGoal.strokeColor;

        const clientValueGoal = dataPoint.goals[1];
        const clientValue = convertNumber(clientValueGoal.value, precision, false);
        const clientValueName = clientValueGoal.name;
        const clientColor = clientValueGoal.strokeColor;

        return `
            <div class="apexcharts-tooltip-title">
                ${dataPoint.x}
            </div>

            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} %</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${meanValue} %</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${clientValue} %</span>
                    </div>

                </div>
            </div>
        `;
    };
    public rateComparisonChartError: boolean = true;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_AERIAL_BARE_NETWORK_RATE_MAP),
    ) as Signal<any[]>;

    public legendConfig: LegendConfig[] = [
        {
            title: 'Taux BT aériens nus',
            values: [
                { color: MapColors.green, value: '0 à 1%' },
                { color: MapColors.lightGreen, value: '1 à 5%' },
                { color: MapColors.paleGreen, value: '5 à 10%' },
                { color: MapColors.yellow, value: '10 à 20%' },
                { color: MapColors.orange, value: '20 à 30%' },
                { color: MapColors.red, value: 'Plus de 30%' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        //GenericComponent intialize sideNav, Tabs (if exists), title and subTitle
        super(route, sidenavService, store);
        effect(() => {
            const averageAgesChartData = this.averageAgesChartDataSignal();
            this.averageAgesChartError = this.checkForError(averageAgesChartData, [
                'xYear',
                'totalLvNetworkAges',
                'overheadLvNetworkAges',
                'overheadLvNetworkAges',
                'twistedLvNetworkAges',
                'undergroundLvNetworkAges',
            ]);
            if (!this.averageAgesChartError) {
                this.averageAgesChartSeries = [
                    {
                        name: this.texts.lvNetworksAverageAgesChart.totalLvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.totalLvNetworkAges,
                        })),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.lvNetworksAverageAgesChart.overheadLvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.overheadLvNetworkAges,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lvNetworksAverageAgesChart.twistedLvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.twistedLvNetworkAges,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.lvNetworksAverageAgesChart
                            .undergroundLvNetworkAgesAxisTitle,
                        type: 'line',
                        data: averageAgesChartData.map((data) => ({
                            x: data.xYear,
                            y: data.undergroundLvNetworkAges,
                        })),
                        color: colorAec1BlueLight,
                    },
                ];
                this.averageAgesChartDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const evolLengthChartData = this.evolLengthChartDataSignal();
            this.evolLengthChartError = this.checkForError(evolLengthChartData, [
                'xYear',
                'bareOverheadLvLengthEr',
                'bareOverheadLvLengthRu',
            ]);
            if (!this.evolLengthChartError) {
                const dataMin = evolLengthChartData.reduce(
                    (min, d) =>
                        Math.min(
                            min,
                            (d.bareOverheadLvLengthEr ?? 0) + (d.bareOverheadLvLengthRu ?? 0),
                        ),
                    Infinity,
                );

                this.evolLengthChartSeries = [
                    {
                        name: this.texts.lvNetworksEvolOverheadChart
                            .bareOverheadLvLengthErAxisTitle,
                        type: 'bar',
                        data: evolLengthChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.bareOverheadLvLengthEr,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lvNetworksEvolOverheadChart
                            .bareOverheadLvLengthRuAxisTitle,
                        type: 'bar',
                        data: evolLengthChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.bareOverheadLvLengthRu,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lvNetworksEvolOverheadChart.axisTitleRate,
                        type: 'bar',
                        data: evolLengthChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: dataMin * 0.5,
                        })),
                        color: 'transparent',
                    },
                ];
                this.evolLengthChartDataLabels = this.setEvolutionDataLabels(
                    evolLengthChartData,
                    'bareOverheadNetworkLvRate',
                );
            }

            const evolLengthFsChartData = this.evolLengthFsChartDataSignal();
            this.evolLengthFsChartError = this.checkForError(evolLengthFsChartData, [
                'xYear',
                'lowSectionLvLengthEr',
                'lowSectionLvLengthRu',
            ]);
            const minData = evolLengthFsChartData.reduce(
                (min, d) =>
                    Math.min(min, (d.lowSectionLvLengthEr ?? 0) + (d.lowSectionLvLengthRu ?? 0)),
                Infinity,
            );
            if (!this.evolLengthFsChartError) {
                this.evolLengthFsChartSeries = [
                    {
                        name: this.texts.lvNetworksEvolOverheadFsChart
                            .lowSectionLvLengthErAxisTitle,
                        type: 'bar',
                        data: evolLengthFsChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.lowSectionLvLengthEr,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lvNetworksEvolOverheadFsChart
                            .lowSectionLvLengthRuAxisTitle,
                        type: 'bar',
                        data: evolLengthFsChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.lowSectionLvLengthRu,
                        })),
                        color: colorAec3Green,
                    },

                    {
                        name: this.texts.lvNetworksEvolOverheadFsChart.axisTitleRate,
                        type: 'bar',
                        data: evolLengthFsChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: minData,
                        })),
                        color: 'transparent',
                    },
                ];
                this.evolLengthFsChartDataLabels = this.setEvolutionDataLabels(
                    evolLengthFsChartData,
                    'lowSectionLvNetworkPercentage',
                );
            }

            const rateComparisonChartData = this.rateComparisonChartDataSignal();
            this.rateComparisonChartError = this.checkForError(rateComparisonChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.rateComparisonChartError) {
                this.rateComparisonChartSeries = [
                    {
                        name: this.texts.lvNetworksRateComparisonChart.serieLabel,
                        data: rateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.lvNetworksRateComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.lvNetworksRateComparisonChart.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.lvNetworksRateComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.lvNetworksRateComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.evolLengthCustomTooltip = this.setSimpleCustomTooltip(0, [0, 1]);
        this.evolLengthFsCustomTooltip = this.setSimpleCustomTooltip(0, [0, 1]);
    }

    public setEvolutionDataLabels(data: any[], rateKey: string): ApexDataLabels {
        return {
            enabled: true,
            enabledOnSeries: [0, 1, 2],
            formatter: (val: number, opts: any) => {
                const seriesIndex = opts.seriesIndex;
                const dataIndex = opts.dataPointIndex;

                if ([0, 1].includes(seriesIndex)) {
                    return `${convertNumber(val, 0)}`;
                }

                if (seriesIndex === 2) {
                    const percentage = data[dataIndex]?.[rateKey];
                    return `${convertNumber(percentage, 1)}%`;
                }

                return '';
            },
            textAnchor: 'middle',
            style: {
                colors: [colorAec1BlueLight, colorAec3Green, colorAec2BlueDark],
                fontSize: '11px',
            },
            background: {
                enabled: true,
                opacity: 0.5,
                foreColor: 'white',
            },
            distributed: false,
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['lvAerialBareNetworkRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColors.green;
        const lvAerialBareNetworkRate = Number(feature.properties['lvAerialBareNetworkRate']);
        if (lvAerialBareNetworkRate > 30) {
            fillColor = MapColors.red;
        } else if (lvAerialBareNetworkRate > 20) {
            fillColor = MapColors.orange;
        } else if (lvAerialBareNetworkRate > 10) {
            fillColor = MapColors.yellow;
        } else if (lvAerialBareNetworkRate > 5) {
            fillColor = MapColors.paleGreen;
        } else if (lvAerialBareNetworkRate > 1) {
            fillColor = MapColors.lightGreen;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties || feature.properties['isInConcession'] === false) {
            return '';
        }
        let { cityName, lvAerialBareNetworkRate } = feature.properties;
        return `${cityName} - ${convertNumber(lvAerialBareNetworkRate)} % BT aériens nus`;
    }
}
